import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, CircularProgress, Alert } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import AthleteForm from '../../components/athletes/AthleteForm';
import { getAthlete, updateAthlete } from '../../services/athleteService';

function EditAthleteInner() {
  const { athleteId } = useParams();
  const navigate = useNavigate();
  const [athlete, setAthlete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    getAthlete(athleteId)
      .then(setAthlete)
      .catch(e => setLoadError(e.message))
      .finally(() => setLoading(false));
  }, [athleteId]);

  const handleSubmit = async (values) => {
    setSaveError(null);
    setSaving(true);
    try {
      // Name/DOB/grade/school-context only. height_in/weight_lb are
      // intentionally absent here — they're growth history now (see the
      // Measurements section on the Athlete Profile page), not a profile
      // field this form overwrites. Sending them through this call would
      // be a silent no-op anyway: created_by, linked_user_id, height_in,
      // and weight_lb are all pinned server-side against this UPDATE path
      // (see the ffn_normalize_athlete_update trigger), so there's no way
      // for this form to affect them even if it tried. State/county/
      // school district/ZIP (011) aren't pinned — ordinary correctable
      // profile fields, same as name/grade. county_id/school_district_id
      // are canonical FK references (011) — never free-text names.
      await updateAthlete(athleteId, {
        first_name: values.firstName,
        last_name: values.lastName,
        date_of_birth: values.dateOfBirth,
        grade: values.grade,
        state_code: values.stateCode,
        county_id: values.countyId,
        school_district_id: values.schoolDistrictId,
        zip_code: values.zipCode,
      });
      navigate(`/athletes/${athleteId}`);
    } catch (e) {
      setSaveError(e.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (loadError) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{loadError}</Alert></Container>;

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>Edit Profile</Typography>
      <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
        {athlete.first_name} {athlete.last_name}
      </Typography>
      <AthleteForm
        initialValues={{
          firstName: athlete.first_name,
          lastName: athlete.last_name,
          dateOfBirth: athlete.date_of_birth,
          grade: athlete.grade,
          stateCode: athlete.state_code,
          countyId: athlete.county_id,
          schoolDistrictId: athlete.school_district_id,
          zipCode: athlete.zip_code,
        }}
        onSubmit={handleSubmit}
        submitLabel="Save Changes"
        onCancel={() => navigate(`/athletes/${athleteId}`)}
        saving={saving}
        error={saveError}
      />
    </Container>
  );
}

export default function EditAthletePage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><EditAthleteInner /></RequireAuth>
    </Box>
  );
}
